import dayjs from "dayjs";

import { supabase } from "../supabaseClient";
import { localHourMinute, tzForAirport } from "./airportTz";

// Given a ScheduleMixed-shaped row, find the airport code that the trip
// operates from/to. DEPART rows have the airport on the dropoff side;
// ARRIVE rows have it on the pickup side. Falls back to whichever side is
// a Type-2 (airport) location if the direction isn't obvious. Used so
// downstream helpers can reason about the row in the AIRPORT's timezone
// instead of the viewer's browser timezone.
export function airportCodeForRow(row) {
  const puIsAirport = row?.PickupLocation?.LocationTypeID === 2;
  const doIsAirport = row?.DropoffLocation?.LocationTypeID === 2;
  const transport = (row?.TransportationType?.Code || "").toUpperCase();
  if (transport === "DEPART" && doIsAirport) return (row.DropoffLocation.Code || "").toUpperCase();
  if (transport === "ARRIVE" && puIsAirport) return (row.PickupLocation.Code || "").toUpperCase();
  if (puIsAirport) return (row.PickupLocation.Code || "").toUpperCase();
  if (doIsAirport) return (row.DropoffLocation.Code || "").toUpperCase();
  return "";
}

// Windows match against the row's stored time as-is (no timezone conversion).
// The displayed pickup is just: base time − matching adjustment.

// CrewTimeAdjustments are display-only — they shift the time the crew sees
// for hotel→airport pickups earlier than the schedule's airport drop-off
// time. The database value is never modified; admin views and reports just
// surface the adjusted time alongside the rest of the row.
//
// The base time is ScheduleDate (the imported D/O / flight time). The
// chosen window's adjustment is subtracted from it for display.
//
// Rules:
//   • Only hotel→airport runs are adjusted (TransportationType.Code = "DEPART").
//     Arrivals (airport→hotel) are shown as-scheduled.
//   • JetBlue (B6) is always exempt — its crews schedule their own pickups.
//   • One-off "Add Trip" rows (IsOneOff = true) are never adjusted regardless
//     of airline — the user already entered the time they want shown.
//   • A row uses the first matching window for its manager
//     ([start_minutes, end_minutes); NULL bounds mean "unbounded on that side").

export async function loadAdjustmentsByManager() {
  const { data, error } = await supabase
    .from("CrewTimeAdjustments")
    .select("manager_id, start_minutes, end_minutes, adjustment_minutes");
  if (error) return {};
  const byManager = {};
  (data || []).forEach((a) => {
    if (!byManager[a.manager_id]) byManager[a.manager_id] = [];
    byManager[a.manager_id].push(a);
  });
  return byManager;
}

// The base time used for display. JetBlue (B6) crews schedule their own
// pickups, so we show the imported Pickup Date/Time column directly. All
// other airlines use ScheduleDate (the D/O flight time) and pick up the
// manager's window adjustment on top.
function baseTimeFor(row) {
  const airline = (row?.AirlineLetterCode || "").toUpperCase();
  if (airline === "B6") {
    const raw = row?.PickupDateTime || row?.ScheduleDate;
    return raw ? dayjs(raw) : dayjs(null);
  }
  return row?.ScheduleDate ? dayjs(row.ScheduleDate) : dayjs(null);
}

export function adjustmentMinutesFor(row, adjustmentsByManager) {
  if (!row) return 0;
  if (row.IsOneOff) return 0;
  const transport = (row.TransportationType?.Code || "").toUpperCase();
  if (transport !== "DEPART") return 0;

  const airline = (row.AirlineLetterCode || "").toUpperCase();
  if (airline === "B6") return 0;

  const managerId = row.ManagerID;
  if (!managerId) return 0;

  const list = adjustmentsByManager?.[managerId];
  if (!list || list.length === 0) return 0;

  const base = baseTimeFor(row);
  if (!base.isValid()) return 0;
  // Hour-of-day must be computed in the AIRPORT's timezone — not the
  // viewer's browser. Without this, an ONT window like 4-6am fires (or
  // doesn't) based on whoever is looking at the screen instead of when
  // the flight actually leaves.
  const airportCode = airportCodeForRow(row);
  const { hour, minute } = localHourMinute(base.toDate(), airportCode);
  const minutes = hour * 60 + minute;
  for (const a of list) {
    const lo = a.start_minutes ?? 0;
    const hi = a.end_minutes ?? 1440;
    if (minutes >= lo && minutes < hi) return a.adjustment_minutes || 0;
  }
  return 0;
}

// Returns a dayjs instance for the display time (pickup base time minus the
// matching adjustment). Falls back to the raw base time if anything is
// missing or invalid.
export function adjustedScheduleDayjs(row, adjustmentsByManager) {
  const base = baseTimeFor(row);
  if (!base.isValid()) return base;
  const minutes = adjustmentMinutesFor(row, adjustmentsByManager);
  return minutes > 0 ? base.subtract(minutes, "minute") : base;
}

// Format a UTC instant as "YYYY-MM-DD HH:mm" in an explicit IANA zone.
function formatDateTimeInTz(d, tzName) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: tzName,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(d);
  const get = (t) => parts.find((p) => p.type === t)?.value || "";
  // en-CA renders midnight as "24" in some engines; normalize to "00".
  const hh = get("hour") === "24" ? "00" : get("hour");
  return `${get("year")}-${get("month")}-${get("day")} ${hh}:${get("minute")}`;
}

// Format the adjusted schedule time in EASTERN, for the admin surfaces
// (schedule search + Reports). Two things matter here and they are separate:
//
//   1. WHICH adjustment applies is decided on the AIRPORT'S local hour, up in
//      adjustmentMinutesFor(). ONT's windows are 4-6am -> 30min, 6am-on ->
//      45min; judging those on Eastern hours put a 4:30am Pacific pickup at
//      7:30am and gave it the wrong rule, and pushed late-evening trips past
//      midnight where NO window matched at all.
//   2. HOW the result is displayed is Eastern, always — one company-wide
//      clock so every admin reads the same number.
//
// Never format with the viewer's browser zone. That is what made an MSP row
// read 6:00 for a manager in Florida and 5:00 for one in Minneapolis, off the
// same database row.
export function adjustedScheduleInEastern(row, adjustmentsByManager) {
  const adjusted = adjustedScheduleDayjs(row, adjustmentsByManager);
  if (!adjusted.isValid()) return "";
  return formatDateTimeInTz(adjusted.toDate(), "America/New_York");
}

// Format the adjusted schedule time in the AIRPORT'S OWN timezone as
// "YYYY-MM-DD HH:mm" (24h). This is the number that arrived on the airline's
// sheet: carriers publish ground-transport times in the station's local time
// (an LAX drop-off of 08:05 is 08:05 Pacific), and import stores it as the
// matching UTC instant. Rendering it back in the airport's zone is what makes
// the admin list and Reports read "as it came in".
//
// Never format with the browser's zone — that made an LAX row read 11:05 for
// an Eastern viewer and 08:05 for a Pacific one. If the airport can't be
// resolved we fall back to a FIXED Eastern zone, so the value is at least
// stable and the same for every viewer.
export function adjustedScheduleInAirportTz(row, adjustmentsByManager) {
  const adjusted = adjustedScheduleDayjs(row, adjustmentsByManager);
  if (!adjusted.isValid()) return "";
  return formatDateTimeInTz(adjusted.toDate(), tzForAirport(airportCodeForRow(row)));
}
